import { Button, Group, Modal, Select, Stack, Textarea } from '@mantine/core';
import { useCallback, useState } from 'react';
import type { AddendumReasonCode } from './types';
import { ADDENDUM_REASON_OPTIONS } from './types';

export interface AddendumReasonModalProps {
  opened: boolean;
  onClose: () => void;
  /** Called with selected reason and optional comments. Parent stores comments in body alongside structured line changes. */
  onSubmit: (reason: AddendumReasonCode, comments: string) => void;
}

/**
 * Modal shown during Revise Note after quality checks pass and before re-signing.
 * User selects a reason (required) and may add optional comments; these are included in the addendum.
 */
export function AddendumReasonModal({
  opened,
  onClose,
  onSubmit,
}: AddendumReasonModalProps): JSX.Element {
  const [reasonForChange, setReasonForChange] = useState<AddendumReasonCode | null>(null);
  const [comments, setComments] = useState('');

  const reset = useCallback(() => {
    setReasonForChange(null);
    setComments('');
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleSubmit = useCallback(() => {
    if (!reasonForChange) return;
    onSubmit(reasonForChange, comments.trim());
    reset();
    // Parent closes via `opened={false}` — do not call onClose (cancel path).
  }, [reasonForChange, comments, onSubmit, reset]);

  const canSubmit = reasonForChange != null;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Add revised note for this change"
      size="md"
      centered
    >
      <Stack gap="md">
        <Select
          label="Reason for change"
          placeholder="Select a reason"
          data={ADDENDUM_REASON_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          value={reasonForChange}
          onChange={(v) => setReasonForChange((v as AddendumReasonCode) ?? null)}
          required
        />

        <Textarea
          label="Comments (optional)"
          placeholder="Add any comments about the changes you made..."
          value={comments}
          onChange={(e) => setComments(e.currentTarget.value)}
          minRows={3}
        />

        <Group justify="flex-end" gap="sm" mt="sm">
          <Button variant="default" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            Add revised note
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
